import { useState, useEffect } from "react";
import { ConfirmationResult } from "firebase/auth";
import { isValidPhoneNumber } from "libphonenumber-js";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PhoneNumberInput } from "@/components/ui/phone-input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { Loader2, Phone, MessageSquare } from "lucide-react";
import { useUser } from "@/contexts/UserContext";
import { useExpense } from "@/contexts/ExpenseContext";
import { useTranslation } from "react-i18next";
import { FirebaseSetupRequired } from "@/components/FirebaseSetupRequired";
import { auth, IS_MOCK_MODE } from "@/lib/firebase";
import { TEST_PHONE_NUMBERS, TEST_OTP_CODE } from "@/lib/mockAuth";

export default function Login() {
  const { sendOTP, verifyOTP, user, loading: userLoading } = useUser();
  const { clearAuthError } = useExpense();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Redirect to dashboard if user is already logged in
  useEffect(() => {
    if (user) {
      console.log('🔄 User already logged in, redirecting to dashboard...');
      navigate('/dashboard', { replace: true });
    }
  }, [user, navigate]);
  
  const [phoneNumber, setPhoneNumber] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState<"phone" | "otp">("phone");
  const [confirmationResult, setConfirmationResult] = useState<ConfirmationResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  
  // Check if Firebase is configured
  if (!auth) {
    return <FirebaseSetupRequired />;
  }



  const handleSendOTP = async () => {
    setError("");
    
    // Validate phone number
    if (!phoneNumber.trim()) {
      setError(t('auth.phoneNumberRequired'));
      return;
    }

    try {
      let formattedPhoneNumber = phoneNumber;

      // Only validate phone number for real Firebase
      if (!IS_MOCK_MODE) {
        if (!isValidPhoneNumber(phoneNumber || '')) {
          setError(t('auth.invalidPhoneNumber'));
          return;
        }
        formattedPhoneNumber = phoneNumber; // react-phone-number-input already formats it
      }

      setLoading(true);
      
      const confirmation = await sendOTP(formattedPhoneNumber);
      setConfirmationResult(confirmation);
      setStep("otp");
      
    } catch (error: unknown) {
      console.error('Error sending OTP:', error);
      setError(error instanceof Error ? error.message : 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (otpValue?: string) => {
    if (!confirmationResult) return;
    
    const otpToVerify = otpValue || otp;
    setError("");
    
    if (!otpToVerify.trim() || otpToVerify.length !== 6) {
      setError(t('auth.invalidOTP'));
      return;
    }

    try {
      setLoading(true);
      await verifyOTP(confirmationResult, otpToVerify);
      
      // Clear any authentication errors after successful login
      clearAuthError();
      
      // Redirect to dashboard after successful OTP verification
      navigate('/dashboard', { replace: true });
    } catch (error: unknown) {
      console.error('Error verifying OTP:', error);
      setError(t('auth.invalidOTP'));
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (value: string) => {
    setOtp(value);
    setError(""); // Clear any previous errors when user types
    
    // Auto-verify when 6 digits are entered
    if (value.length === 6 && !loading) {
      handleVerifyOTP(value);
    }
  };

  const handleResendOTP = async () => {
    setOtp("");
    setStep("phone");
    setConfirmationResult(null);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="w-full max-w-md border-border/90">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 w-12 h-12 bg-primary/20 border border-primary/35 rounded-full flex items-center justify-center">
            {step === "phone" ? (
              <Phone className="h-6 w-6 text-primary-foreground" />
            ) : (
              <MessageSquare className="h-6 w-6 text-primary-foreground" />
            )}
          </div>
          <CardTitle className="text-2xl font-bold">
            {step === "phone" ? t('auth.login') : t('auth.verifyOTP')}
          </CardTitle>
          <CardDescription>
            {step === "phone" 
              ? t('auth.enterPhoneNumber')
              : t('auth.enterOTP')
            }
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-4">
          {IS_MOCK_MODE && (
            <Alert>
              <AlertDescription className="text-sm">
                <strong>🔧 Development Mode:</strong><br/>
                Phone: <code>+972501234567</code> | OTP: <code>123456</code>
              </AlertDescription>
            </Alert>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          {step === "phone" ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="phone">{t('auth.phoneNumber')}</Label>
                <PhoneNumberInput
                  value={phoneNumber}
                  onChange={(value) => setPhoneNumber(value || '')}
                  placeholder={IS_MOCK_MODE ? "+972501234567" : "Enter phone number"}
                  className="mt-1"
                  error={!!error && step === "phone"}
                />
              </div>
              
              <Button 
                onClick={handleSendOTP} 
                disabled={loading}
                className="w-full"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  t('auth.sendOTP')
                )}
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="text-center">
                <p className="text-sm text-muted-foreground mb-4">
                  {t('auth.otpSent')} {phoneNumber}
                </p>
                
                <div className="flex justify-center">
                  <InputOTP
                    maxLength={6}
                    value={otp}
                    onChange={handleOTPChange}
                    disabled={loading}
                  >
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>
              </div>
              
              {loading ? (
                <div className="flex items-center justify-center py-3">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  <span className="text-sm text-muted-foreground">
                    {t('auth.verifying', 'Verifying...')}
                  </span>
                </div>
              ) : (
                <Button 
                  onClick={() => handleVerifyOTP()} 
                  disabled={otp.length !== 6}
                  variant="outline"
                  className="w-full"
                >
                  {t('auth.verifyOTP')}
                </Button>
              )}
              
              <p className="text-xs text-center text-muted-foreground mt-2">
                {t('auth.autoVerifyHint', 'Code will be verified automatically when 6 digits are entered')}
              </p>
              
              <Button 
                variant="outline" 
                onClick={handleResendOTP}
                className="w-full"
              >
                {t('auth.resendOTP')}
              </Button>
            </div>
          )}
          
          {/* reCAPTCHA container */}
          <div id="recaptcha-container"></div>
        </CardContent>
      </Card>
    </div>
  );
} 